/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { CSSProperties } from 'react';
import { get } from 'lodash';
import { Paper } from '@material-ui/core';
import {
  CheckBox,
  CheckBoxOutlineBlank,
  ToggleOff,
  ToggleOn,
} from '@material-ui/icons';
import { getEntries, getPartialTimeString } from 'Utils';
import { TranslateFunction } from 'Types';
import { getDates } from 'components/HoursOfOperation/Types';
import { CommercialMechanicalServices, CompleteMechanical } from 'components/CommercialServices/CommercialServicesFields';
import { getOptions } from '@michelin/select-options-provider';
import { getFieldLabel, mainFieldNames } from './FieldNames';
import { fieldsToIgnore } from './ChangeLog';

interface RoleArea {
  role_area: string;
  levels: { level: string, location: string }[];
}
interface ContactType {
  id: string;
  role_areas: RoleArea[];
  service: string;
}
interface NotificationSubscription {
  id: string;
  cc_mails: string[];
  event: string;
  method: string[];
  type: string;
}

const VALIDATION_REM_COLOR = 'rgb(241, 111, 111)';
const VALIDATION_ADD_COLOR = 'rgb(102, 175, 78)';

export const renderHoursOfOperations = (hoo: string, countryCode: string, t: TranslateFunction) => {
  const workHours: { key: string, label: string, value: string | JSX.Element }[] = [];
  const hooDays = getDates(countryCode.toLowerCase());
  if (!hoo) return <div />;
  Object.entries(hoo).forEach(([key, val]) => {
    let time: string | JSX.Element = '';

    if (val && Object.keys(val).length !== 0 && (val as any).begin !== undefined && (val as any).end !== undefined) {
      const begin = ((val as any).begin || '').trim();
      const end = ((val as any).end || '').trim();

      if (!end) time = `Opens at ${getPartialTimeString(begin)}`;
      if (!begin) time = `Closes at ${getPartialTimeString(end)}`;

      if (time === '') time = `${getPartialTimeString(begin)} to ${getPartialTimeString(end)}`;
    } else {
      time = (<span style={{ color: '#888', fontStyle: 'italic' }}>{t('Closed')}</span>);
    }

    workHours.push({
      key,
      label: t(hooDays.find((day) => day.key === key)?.label || ''),
      value: time,
    });
  });

  // order the workHours array by the Hour of Operation Days
  const orderedWorkHours = workHours.sort((a, b) => {
    const aIndex = hooDays.findIndex((day) => day.key === a.key);
    const bIndex = hooDays.findIndex((day) => day.key === b.key);

    return aIndex - bIndex;
  });

  return (
    <>
      {orderedWorkHours.map((item) => (
        <div key={item.label} style={{ paddingLeft: 5, display: 'grid', gridTemplateColumns: '1fr 10px 2fr' }}>
          <div>{item.label}</div>
          <div>:</div>
          <div>{item.value}</div>
        </div>
      ))}
    </>
  );
};

// let changeId = 0;

const renderContactTypes = (values: ContactType[], t: TranslateFunction, data?: any, field?: string, isCreated?: boolean) => {
  if (!values || values.length === 0) return (<div />);
  let contactTypes: ContactType[] = [];
  const options = getOptions('dealer_dealer_contact_types_role_areas');

  if (isCreated) {
    contactTypes = [...values];
  } else {
    const dataToFilter = data.changes.map((change: string) => change.split('.').slice(0, 3).join('.')).filter((item: string) => item.indexOf('contact_types') > -1);
    const newChanges = dataToFilter.filter((e: string, i: number) => dataToFilter.indexOf(e) === i);

    newChanges.forEach((change: string) => {
      // const changesToGet = change.split('.').slice(0, 3).join('.');
      const newData = get((field === 'origin' ? data.before : data.after), change);
      contactTypes.push(newData);
    });
  }

  return (
    <div style={{ paddingLeft: 5 }}>
      {contactTypes.map((item) => (
        <>
          <div style={{ marginRight: '10px' }}>{t(getFieldLabel(item?.service) || item?.service)}</div>
          <ul style={{ margin: '0px' }}>{item?.role_areas?.map((roleArea) => {
            const label = options.filter((i) => i?.value === roleArea?.role_area)[0]?.label;
            const level = roleArea?.levels.map((lvl) => `(${lvl?.level}${(lvl?.location ? ` ${lvl?.location?.split('~').pop()}` : '')})`).join(' | ');
            return (<li>{label}<span style={{ color: '#888', fontStyle: 'italic' }}>{level}</span></li>);
          })}
          </ul>
        </>
      ))}
    </div>
  );
};

const renderListOfStrings = (values: any) => {
  const newValues = values.map((item: any) => (item.replace('- ', '').replace('+ ', ''))).filter((item: string) => (item !== ' '));
  return (
    <>
      {newValues.map((item: string, i: number) => (
        <ul>
          <li style={{ fontStyle: 'italic' }} key={`${i}${item}`}>{item.trim()} </li>
        </ul>
      ))}
    </>
  );
};

const renderNotificationSubscription = (nSubscription: NotificationSubscription[], t: TranslateFunction) => {
  if (!nSubscription || nSubscription.length === 0) return (<div />);

  const listStyle: CSSProperties = {
    marginLeft: 16,
    listStyleType: 'none',
  };

  return (
    <div style={{ paddingLeft: 5 }}>
      {nSubscription.map((item) => (
        <Paper
          elevation={3}
          style={{
            margin: '5px',
            padding: '5px',
            display: 'grid',
            gridTemplateColumns: '60px 5px 1fr',
          }}
        >
          <div>{t('Event')}</div>
          <div>:</div>
          <div>{item.event}</div>
          <div>Type</div>
          <div>:</div>
          <div>{item.type}</div>
          <div>Methods</div>
          <div>:</div>
          <div>
            {
              item.method?.map((method) => {
                listStyle.color = method.indexOf('+') >= 0 ? VALIDATION_ADD_COLOR : VALIDATION_REM_COLOR;
                return (<li style={listStyle}>{method}</li>);
              })
            }
          </div>
          {(item.cc_mails) && (
            <>
              <div>Mails</div>
              <div>:</div>
              <div>{item.cc_mails?.map((mail) => (<li>{mail}</li>))}</div>
            </>
          )}
        </Paper>
      ))}
    </div>
  );
};

const renderBoolean = (value: boolean, t: TranslateFunction) => {
  if (value === false) return <span style={{ color: VALIDATION_REM_COLOR }}>{t('No')}</span>;
  return <span style={{ color: VALIDATION_ADD_COLOR }}>{t('Yes')}</span>;
};

const renderListOfBooleans = (value: any, t: TranslateFunction) => {
  const items: { label: string, value: string | JSX.Element }[] = [];

  if (value === undefined) return <div />;
  Object.entries(value).forEach(([key, val]) => {
    items.push({
      label: t(getFieldLabel(key)),
      value: renderBoolean((val || false) as boolean, t),
    });
  });

  return (
    <>
      {items.sort().map((item) => (
        <div style={{ paddingLeft: 5, display: 'grid', gridTemplateColumns: '0.95fr 10px' }}>
          <div>{item.label}</div>
          <div>
            <b>{item.value}</b>
          </div>
        </div>
      ))}
    </>
  );
};

const renderCompleteMechanical = (value: CompleteMechanical, t: TranslateFunction) => {
  const items: { label: string, value: string | JSX.Element }[] = [];
  const condition = (val: string) => (val.trim() !== '-' && val.trim() !== '+');

  Object.entries(value.authorized_service_of).forEach(([key, val]) => {
    items.push({
      label: t(getFieldLabel(key)),
      value: (
        <div>
          {
            val?.filter((v) => condition(v)).map((item) => (
              <li key={`${key}_${item}`} style={{ listStyle: 'none', color: item.indexOf('+') >= 0 ? VALIDATION_ADD_COLOR : VALIDATION_REM_COLOR, paddingLeft: '10px' }}>
                <b>{item}</b>
              </li>
            ))
          }
        </div>),
    });
  });

  return (
    <div style={{ paddingLeft: 5 }}>
      {items.map((item) => (
        <>
          <div>{item.label}</div>
          <div>{item.value}</div>
        </>
      ))}
    </div>
  );
};

const renderFullCheckboxLine = (value: any) => {
  if (value === undefined) return '';
  return (!value ? <CheckBoxOutlineBlank color="primary" /> : <CheckBox color="primary" />);
};

const renderProtectionsOrWarranties = (data: any, t: TranslateFunction) => (
  <>
    {data?.road_hazard_protection !== undefined && (
      <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '5px' }}>
        {data.road_hazard_protection ? <CheckBox color="primary" /> : <CheckBoxOutlineBlank color="primary" />} &nbsp; {t('Road Hazard Protection')}
      </div>
    )}
    {data?.tire_warranty && typeof data.tire_warranty === 'string' && data.tire_warranty !== '' && (
      <div style={{ paddingLeft: '5px' }}>
        <b>{t('Tire Warranty')}</b>: <span style={{ fontStyle: 'italic' }}> {(data.tire_warranty.toString()).replaceAll('_', ' ')} </span>
      </div>
    )}
  </>
);

const renderFullCheckbox = (value: any, type: string, title: string, t: TranslateFunction, suffix: string = '') => {
  const headerStyle = { justifySelf: 'center', alignSelf: 'center', fontSize: '10px' };
  const contentStyle = { justifySelf: 'center', alignSelf: 'center', color: VALIDATION_ADD_COLOR };
  const titles = [];

  if (type === 'trailer') {
    titles[0] = 'Trailers < 10,000 lbs';
    titles[1] = 'Trailers >= 10,000 lbs';
  }

  if (type === 'duty') {
    titles[0] = 'Lt/Md Duty';
    titles[1] = 'Heavy Duty';
  }

  if (type === 'retail') {
    titles[0] = '';
    titles[1] = '';
  }

  const items: {
    label: string;
    col_1: JSX.Element | string,
    col_2: JSX.Element | string,
    col_3: JSX.Element | string,
    col_4: JSX.Element | string,
  }[] = getEntries(value, true).map(({ key, value: val }) => {
    if (val === undefined) {
      return {
        label: t(getFieldLabel(`${key.toString()}${suffix}`)),
        col_1: '',
        col_2: '',
        col_3: '',
        col_4: '',
      };
    }

    if (type === 'trailer') {
      return {
        label: t(getFieldLabel(`${key.toString()}${suffix}`)),
        col_1: renderFullCheckboxLine(val.in_bay?.trailers_less_than_10000_lbs),
        col_2: renderFullCheckboxLine(val.mobile?.trailers_less_than_10000_lbs),
        col_3: renderFullCheckboxLine(val.in_bay?.trailers_greater_than_10000_lbs),
        col_4: renderFullCheckboxLine(val.mobile?.trailers_greater_than_10000_lbs),
      };
    }

    if (type === 'duty') {
      return {
        label: t(getFieldLabel(`${key.toString()}${suffix}`)),
        col_1: renderFullCheckboxLine(val.in_bay?.light_medium_duty),
        col_2: renderFullCheckboxLine(val.mobile?.light_medium_duty),
        col_3: renderFullCheckboxLine(val.in_bay?.heavy_duty),
        col_4: renderFullCheckboxLine(val.mobile?.heavy_duty),
      };
    }

    if (type === 'retail') {
      return {
        label: t(getFieldLabel(`${key.toString()}`)),
        col_1: renderFullCheckboxLine(val.in_bay),
        col_2: renderFullCheckboxLine(val.mobile),
        col_3: '',
        col_4: '',
      };
    }

    return {
      label: t(getFieldLabel(`${key.toString()}${suffix}`)),
      col_1: '',
      col_2: '',
      col_3: '',
      col_4: '',
    };
  });

  const showCols = {
    showCol_1: items.filter((item) => item.col_1 !== '').length > 0,
    showCol_2: items.filter((item) => item.col_2 !== '').length > 0,
    showCol_3: items.filter((item) => item.col_3 !== '').length > 0,
    showCol_4: items.filter((item) => item.col_4 !== '').length > 0,
  };

  // Set the propper style to all the headers to hide and show the columns.
  const gridTemplateColumns = `2fr ${showCols.showCol_1 ? '1fr' : ''} ${showCols.showCol_2 ? '1fr' : ''} ${showCols.showCol_3 ? '1fr' : ''} ${showCols.showCol_4 ? '1fr' : ''}`;
  const colSpan = title === '' ? 'span 1' : 'span 2';
  const title_col_1 = showCols.showCol_1 && showCols.showCol_2 ? colSpan : '';
  const title_col_2 = showCols.showCol_3 && showCols.showCol_4 ? 'span 2' : '';

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns, paddingLeft: '5px' }}>
        {title !== '' && <div style={{ gridRow: 'span 2', alignSelf: 'center', justifySelf: 'center' }}><b>{t(title)}</b></div>}
        {(showCols.showCol_1 || showCols.showCol_2) && (<div style={{ ...headerStyle, gridColumn: title_col_1 }}>{t(titles[0])}</div>)}
        {(showCols.showCol_3 || showCols.showCol_4) && (<div style={{ ...headerStyle, gridColumn: title_col_2 }}>{t(titles[1])}</div>)}
        {showCols.showCol_1 && (<div style={headerStyle}>{t('In Bay')}</div>)}
        {showCols.showCol_2 && (<div style={headerStyle}>{t('Mobile')}</div>)}
        {showCols.showCol_3 && (<div style={headerStyle}>{t('In Bay')}</div>)}
        {showCols.showCol_4 && (<div style={headerStyle}>{t('Mobile')}</div>)}
        {
          items.map((item) => (
            <>
              <div style={{ justifySelf: 'left' }}>{item.label}</div>
              {showCols.showCol_1 && (<div style={contentStyle}>{item.col_1}</div>)}
              {showCols.showCol_2 && (<div style={contentStyle}>{item.col_2}</div>)}
              {showCols.showCol_3 && (<div style={contentStyle}>{item.col_3}</div>)}
              {showCols.showCol_4 && (<div style={contentStyle}>{item.col_4}</div>)}
            </>
          ))
        }
      </div>
    </>
  );
};

const renderLatLon = (value: any) => <span>{parseFloat(value || 0).toFixed(2)}</span>;

const renderServiceRadius = (value: any, t: TranslateFunction, countryCode: string) => {
  let val = '';
  if (countryCode.toLowerCase() === 'us') {
    val = `${(parseInt(value || 0, 10) * 0.000621371).toFixed(2)} ${t('Miles')}`;
  } else {
    val = `${(parseInt(value || 0, 10) / 1000).toFixed(2)} ${t('KM')}`;
  }
  return <span>{val}</span>;
};

const renderFile = (fileName: any) => <span style={{ color: '#888', fontStyle: 'italic' }}>{fileName}</span>;

const renderFiles = (value: any, t: TranslateFunction) => {
  if (value && value.length > 0) {
    return (
      <ul style={{ margin: '0px' }}>
        {value.map((item: any) => {
          if (!item) return null;
          let fileName = t('None');
          if (item.file && Object.hasOwnProperty.call(item.file, 'name')) fileName = item.file.name;
          return <li key="renderFile">{renderFile(fileName)}</li>;
        })}
      </ul>
    );
  }

  let fileName = t('None');
  if (value && Object.hasOwnProperty.call(value, 'name')) fileName = value.name;
  return renderFile(fileName);
};

const renderString = (value: any) => {
  if (!value || Object.keys(value).length === 0) return <span />;
  return (<span>{value}</span>);
};

const renderOverrides = (value: any, data: any, field: string, isValue: boolean, t: TranslateFunction) => {
  let cloneData: any = null;

  if (field === 'origin') cloneData = { ...data.after.overrides };
  if (field === 'diff') cloneData = { ...data.before.overrides };

  if (!cloneData) return <span>&nbsp;</span>;

  const mcsnMember = cloneData.mcsn_member;
  const mechanicalLocator = cloneData.on_mechanical_locator;
  const tireLocator = cloneData.on_tire_locator;

  const options = getOptions('spp_service_rankings_reason');
  const contentStyle = {
    flexGrow: 1,
    justifyContent: 'left',
    alignItems: 'center',
    paddingLeft: '0px',
  };
  const overrides: {
    label: string,
    override: Boolean | null,
    overrideValue: Boolean | null,
    rankingValue: string | null,
  }[] = [];

  Object.entries(value).forEach(([key, val]) => {
    if (key === 'service_tier_ranking_date') return null;
    if (key === 'mcsn_member' && isValue && mcsnMember === false) return null;
    if (key === 'on_mechanical_locator' && isValue && mechanicalLocator === false) return null;
    if (key === 'on_tire_locator' && isValue && tireLocator === false) return null;

    const overrideValue = (isValue ? val as boolean : null);
    const override = (isValue ? true : val as boolean);
    const rankingValue = (key === 'service_tier_ranking_reason' ? options.filter((i) => i.value === val)[0]?.label : val as string);

    if (key.indexOf('service_tier_ranking') > -1) {
      overrides.push({
        label: t(getFieldLabel(key)),
        override: (isValue ? null : override),
        overrideValue: null,
        rankingValue,
      });
    } else {
      overrides.push({
        label: t(getFieldLabel(key)),
        override,
        overrideValue,
        rankingValue: null,
      });
    }

    return true;
  });

  return (
    <>
      {
        overrides.map((item) => {
          const toogleIcon = (item.overrideValue ? <ToggleOn fontSize="large" color="primary" /> : <ToggleOff fontSize="large" color="secondary" />);
          if (item.override === null) contentStyle.paddingLeft = '5px';

          return (
            <div key={`override-${item.label}`} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              {item.override !== null && (item.override ? <CheckBox color="primary" /> : <CheckBoxOutlineBlank color="primary" />)}
              <div style={contentStyle}>{item.label}</div>
              <div style={{ display: 'flex', flexGrow: 0, alignItems: 'center' }}>
                {item.overrideValue !== null && <>{t('No')}{toogleIcon}{t('Yes')}</>}
                {item.rankingValue !== null && <>{item.rankingValue}</>}
              </div>
            </div>
          );
        })
      }
    </>
  );
};

const renderRetailServices = (value: any, t: TranslateFunction) => {
  const items: { label?: string, value: string | JSX.Element }[] = [];
  const cloneValue = { ...value };

  if (!value) return (<div />);

  items.push({
    value: renderFullCheckbox(cloneValue, 'retail', '', t),
  });

  return (
    <>
      {items.map((item, i) => (
        <div style={{ padding: 5 }} key={`render-item-${i}`}>
          {item.label && (<b>{t(item.label)} :</b>)}
          {item.value}
        </div>
      ))}
    </>
  );
};

const renderValue = (key: string, value: string | boolean, countryCode: string, t: TranslateFunction, data?: any, field?: string, isCreated?: boolean): React.ReactElement => {
  if (value === null || value === undefined) return <></>;
  switch (key) {
    case 'after_hours_phone_number': return renderString(value);
    case 'cell_phone': return renderString(value);
    case 'certifications': return renderFiles(value, t);
    case 'outside_certifications': return renderFiles(value, t);
    case 'contact_level': return renderString(value);
    case 'contact_types': return renderContactTypes(value as unknown as ContactType[], t, data, field, isCreated);
    case 'county': return renderString(value);
    case 'create_user': return renderString(value);
    case 'created_date': return renderString(value);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    case 'commercial_information': return renderValues(value, countryCode, t);
    case 'current_version': return renderString(value);
    case 'customer_inter_hwy_info': return renderString(value);
    case 'do_you_have_trucks': return renderBoolean(value as boolean, t);
    case 'oncall_sds_dashboard': return renderBoolean(value as boolean, t);
    case 'is_fixpix_location': return renderBoolean(value as boolean, t);
    case 'email_address': return renderString(value);
    case 'ers_mechanical_services_provided': return renderListOfBooleans(value, t);
    case 'ext': return renderString(value);
    case 'fax': return renderString(value);
    case 'first_name': return renderString(value);
    case 'group': return renderBoolean(value as boolean, t);
    case 'geo_code_latitude': return renderLatLon(value);
    case 'geo_code_longitude': return renderLatLon(value);
    case 'geocode_updated_by_user': return renderString(value);
    case 'hours_of_operation': return renderHoursOfOperations(value as string, countryCode, t);
    case 'is_deleted': return renderBoolean(value as boolean, t);
    case 'is_mobile_only': return renderBoolean(value as boolean, t);
    case 'mobile_only': return renderBoolean(value as boolean, t);
    case 'job_title': return renderString(value);
    case 'last_name': return renderString(value);
    case 'location_assigned': return renderString(value);
    case 'mechanical_care_dealer': return renderBoolean(value as boolean, t);
    case 'megamile_retreads': return renderBoolean(value as boolean, t);
    case 'mrt_dealer': return renderBoolean(value as boolean, t);
    case 'customer_type': return renderString(value);
    case 'notification_subscription': return renderNotificationSubscription(value as unknown as NotificationSubscription[], t);
    case 'number_of_trucks': return renderString(value.toString());
    case 'requested_geo_code_explanation': return renderString(value);
    case 'requested_geo_code_latitude': return renderLatLon(value);
    case 'requested_geo_code_longitude': return renderLatLon(value);
    case 'requested_geo_code_update': return renderBoolean(value as boolean, t);
    case 'oliver_retreads': return renderBoolean(value as boolean, t);
    case 'open_for_business': return renderBoolean(value as boolean, t);
    case 'open_for_commercial_business': return renderBoolean(value as boolean, t);
    case 'photo_of_truck': return renderFiles(value, t);
    case 'preferred_language': return renderString(value);
    case 'preferred_method': return renderString(value);
    case 'provides_247365_ers_service': return renderBoolean(value as boolean, t);
    case 'mechanical_same_hours_as_tires_ers_services': return renderBoolean(value as boolean, t);
    case 'secondary_after_hours_phone_number': return renderString(value);
    case 'service_address_latitude': return renderLatLon(value);
    case 'service_address_longitude': return renderLatLon(value);
    case 'service_address': return renderString(value);
    case 'service_city': return renderString(value);
    case 'service_country': return renderString(value);
    case 'service_radius': return renderServiceRadius(value, t, countryCode);
    case 'service_request_email': return renderString(value);
    case 'service_state': return renderString(value);
    case 'service_zip': return renderString(value);
    case 'site_url': return renderString(value);
    case 'st_phone': return renderString(value);
    case 'store_email': return renderString(value);
    case 'tire_care_member': return renderBoolean(value as boolean, t);
    case 'toll_free_number': return renderString(value);
    case 'ultra_low_profile_wide_design_retreads': return renderBoolean(value as boolean, t);
    case 'visible_externally': return renderBoolean(value as boolean, t);
    case 'work_hours': return renderHoursOfOperations(value as string, countryCode, t);
    case 'work_phone': return renderString(value);
    case 'x_one_tires': return renderBoolean(value as boolean, t);
    case 'pos_provider': return renderString(value);
    case 'customer_number_with_pos_provider': return renderString(value);
    case 'google_places_id': return renderString(value);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    case 'pos_details': return renderValues(value as string, countryCode, t);
    case 'pos_details_updated_by_user': return renderString(value);
    case 'pos_provider_technical_contact_name': return renderString(value);
    case 'pos_provider_technical_contact_email': return renderString(value);
    case 'pos_provider_technical_contact_phone': return renderString(value);
    case 'has_pos_system': return renderBoolean(value as boolean, t);

    case 'rv_dealer': return renderBoolean(value as boolean, t);
    case 'motor_coach_dealer': return renderBoolean(value as boolean, t);

    case 'overrides': return renderOverrides(value, data, field || '', false, t);
    case 'overrides_value': return renderOverrides(value, data, field || '', true, t);
    case 'calc_onlocator_tires': return renderBoolean(value as boolean, t);
    case 'calc_onlocator_mechanical': return renderBoolean(value as boolean, t);
    case 'tire_services_hours_of_operation': return renderHoursOfOperations(value as string, countryCode, t);
    case 'mechanical_hours_of_operation': return renderHoursOfOperations(value as string, countryCode, t);
    case 'mcsn': return renderBoolean(value as boolean, t);
    case 'service_tier_ranking': return renderString(value);

    case 'programs': return renderListOfBooleans(value, t);
    case 'languages': return renderListOfBooleans(value, t);
    case 'customer_convenience': return renderListOfBooleans(value, t);
    case 'vehicle_types_serviced': return renderListOfBooleans(value, t);
    case 'ers_services': return renderListOfBooleans(value, t);
    case 'brands': return renderListOfStrings(value);
    case 'all_brands': return renderBoolean(value as boolean, t);
    case 'mechanical_services': return renderRetailServices(value, t);
    case 'tire_services': return renderRetailServices(value, t);

    case 'protections_and_warranties': return renderProtectionsOrWarranties(value, t);

    case 'retail_service_radius': return renderServiceRadius(value, t, countryCode);

    case 'michelin_promo': return renderString(value);
    case 'bfg_promo': return renderString(value);
    case 'primary_st_locations': return renderListOfStrings(value);
    case 'secondary_st_locations': return renderListOfStrings(value);

    default: return <span>{JSON.stringify(value)}</span>;
  }
};

export const renderValues = (value: any, countryCode: string, t: TranslateFunction, isDeleted: boolean = false, isCreated: boolean = false, data?: any, field?: string) => {
  const items: { label: string, value: string | JSX.Element }[] = [];

  if (!value) return (<span>&nbsp;</span>);

  if (isDeleted === true) {
    items.push({
      label: (mainFieldNames as any).is_deleted || 'is_deleted',
      value: renderValue('is_deleted', (value as any).is_deleted, countryCode, t),
    });
  } else {
    Object.entries(value).sort().forEach(([key, val]) => {
      if (!val && typeof value === 'boolean') return;
      items.push({
        label: t(getFieldLabel(key)),
        value: renderValue(key, val as string, countryCode, t, data, field, isCreated),
      });
    });
  }

  return (
    <>
      {items.map((item, i) => (
        <div style={{ paddingLeft: 5 }} key={`render-item-${i}`}>
          <b>{t(item.label)}</b>: {item.value}
        </div>
      ))}
    </>
  );
};

export const renderMechanicalServices = (value: CommercialMechanicalServices, t: TranslateFunction) => {
  if (!value) return <span>&nbsp;</span>;

  const items: { label?: string, value: string | JSX.Element }[] = [];

  if ('number_of_dedicated_mobile_mechanical_units' in value) {
    items.push({
      label: t('# of Dedicated Mobile Mechanical Units'),
      value: <span> {value.number_of_dedicated_mobile_mechanical_units}</span>,
    });
  }

  if (Object.hasOwnProperty.call(value, 'ase_certified_mechanics')) {
    items.push({
      label: t('ASE Certified Mechanics'),
      value: renderListOfBooleans(value.ase_certified_mechanics, t),
    });
  }

  if (Object.hasOwnProperty.call(value, 'basic_mechanical')) {
    items.push({
      value: renderFullCheckbox(value.basic_mechanical, 'trailer', 'Level I - Trailer', t),
    });
  }

  if (Object.hasOwnProperty.call(value, 'light_mechanical')) {
    items.push({
      value: renderFullCheckbox(value.light_mechanical, 'duty', 'Level II - Power Unit', t, '_pu'),
    });
  }

  if (Object.hasOwnProperty.call(value, 'wet_mechanical')) {
    items.push({ label: t('Level III - Fluids'), value: '' });

    if (Object.hasOwnProperty.call(value.wet_mechanical, 'refills_top_offs')) {
      items.push({
        value: renderFullCheckbox(value.wet_mechanical.refills_top_offs, 'duty', 'Refills/Top Offs', t),
      });
    }

    if (Object.hasOwnProperty.call(value.wet_mechanical, 'flushes')) {
      items.push({
        value: renderFullCheckbox(value.wet_mechanical.flushes, 'duty', 'Flushes', t),
      });
    }

    if (Object.hasOwnProperty.call(value.wet_mechanical, 'other_services')) {
      items.push({
        value: renderFullCheckbox(value.wet_mechanical.other_services, 'duty', 'Other Services', t),
      });
    }
  }

  if (Object.hasOwnProperty.call(value, 'full_mechanical')) {
    interface FullMechanical {
      mobile: {
        light_medium_duty: boolean,
        heavy_duty: boolean,
      },
      in_bay: {
        light_medium_duty: boolean,
        heavy_duty: boolean,
      }
    }
    const checkboxList: { [k: string]: FullMechanical } = {};

    if (value.full_mechanical?.electrical_diagnostics) checkboxList.electrical_diagnostics = value.full_mechanical.electrical_diagnostics;
    if (value.full_mechanical.engine_diagnostics?.basic) checkboxList.engine_diagnostics_basic = value.full_mechanical.engine_diagnostics.basic;
    if (value.full_mechanical.engine_diagnostics?.advanced) checkboxList.engine_diagnostics_advanced = value.full_mechanical.engine_diagnostics.advanced;
    if (value.full_mechanical.transmission?.basic) checkboxList.transmission_basic = value.full_mechanical.transmission.basic;
    if (value.full_mechanical.transmission?.advanced) checkboxList.transmission_advanced = value.full_mechanical.transmission.advanced;

    items.push({
      value: renderFullCheckbox(checkboxList, 'duty', 'Level IV - Full', t),
    });
  }

  if (Object.hasOwnProperty.call(value, 'complete_mechanical')) {
    items.push({
      label: t('Complete Mechanical'),
      value: renderCompleteMechanical(value.complete_mechanical, t),
    });
  }

  if (Object.hasOwnProperty.call(value, 'towing')) {
    items.push({
      label: t('Towing'),
      value: renderListOfBooleans(value.towing, t),
    });
  }

  return (
    <>
      {items.map((item, i) => (
        <div style={{ padding: 5 }} key={`render-item-${i}`}>
          {item.label && (<b>{t(item.label)} :</b>)}
          {item.value}
        </div>
      ))}
    </>
  );
};

export const renderTireServices = (value: any, t: TranslateFunction) => {
  const items: { label?: string, value: string | JSX.Element }[] = [];
  const cloneValue = { ...value };

  if (!value) return (<div />);

  if (Object.hasOwnProperty.call(cloneValue, 'wheel_refurbishing')) {
    const key = 'wheel_refurbishing';
    items.push({
      label: t(getFieldLabel(key)),
      value: renderListOfBooleans(cloneValue.wheel_refurbishing, t),
    });

    delete cloneValue.wheel_refurbishing;
  }

  if (Object.hasOwnProperty.call(cloneValue, 'tia_certified_technicians')) {
    const boolList: { [k: string]: boolean } = {};

    if (cloneValue.tia_certified_technicians.training_200) {
      if (Object.hasOwnProperty.call(cloneValue.tia_certified_technicians.training_200, 'ats')) boolList.training_200_ats = cloneValue.tia_certified_technicians.training_200.ats;
      if (Object.hasOwnProperty.call(cloneValue.tia_certified_technicians.training_200, 'cts')) boolList.training_200_cts = cloneValue.tia_certified_technicians.training_200.cts;
    }

    if (cloneValue.tia_certified_technicians.certificate_or_equivalent_300) {
      // eslint-disable-next-line max-len
      if (Object.hasOwnProperty.call(cloneValue.tia_certified_technicians.certificate_or_equivalent_300, 'ats')) boolList.certificate_or_equivalent_300_ats = cloneValue.tia_certified_technicians.certificate_or_equivalent_300.ats;
    }

    const key = 'tia_certified_technicians';
    items.push({
      label: t(getFieldLabel(key)),
      value: renderListOfBooleans(boolList, t),
    });

    delete cloneValue.tia_certified_technicians;
  }

  items.push({
    value: renderFullCheckbox(cloneValue, 'duty', 'Services', t),
  });

  return (
    <>
      {items.map((item, i) => (
        <div style={{ padding: 5 }} key={`render-item-${i}`}>
          {item.label && (<b>{t(item.label)} :</b>)}
          {item.value}
        </div>
      ))}
    </>
  );
};

export const renderContacts = (value: any, data: any, field: any, countryCode: string, t: TranslateFunction) => {
  let key = '';

  Object.keys(value).forEach((k) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (uuidRegex.test(k)) key = k;
  });

  const contactName = `${data.after[key].first_name} ${data.after[key].last_name}`;
  const isCreated = data.changes.includes(key);
  const isDeleted = data.after[key].is_deleted;
  const entries = (isCreated ? data.after[key] : Object.entries(value)[0][1]);
  fieldsToIgnore.forEach((f) => delete entries[f]);
  if ((field === 'origin' && isCreated)) return (<span>&nbsp;</span>);

  return (
    <>
      <div style={{ paddingLeft: 5 }} key="render-contact">
        <span style={{ color: '#27509b' }}><b>{contactName}</b></span>: <br />{renderValues(entries as string, countryCode, t, isDeleted, isCreated, data, field)}
      </div>
    </>
  );
};
